import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const optionValue = option => option?.value ?? option?.id ?? option;

const ChosenSelect = ({
  options,
  value,
  multiple = false,
  className,
  placeholder = '',
  onChange,
  renderOption = option => option.title || (option.value ?? option.id),
  renderGroup,
  testId,
  id = testId,
}) => {
  const selectRef = useRef(null);
  const chosenRef = useRef(null);
  const [usesNativeSelect, setUsesNativeSelect] = useState(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const select = selectRef.current;
    const $ = window.jQuery || window.$;
    if (!select) return undefined;

    const handleChange = () => {
      const selectedValues = Array.from(select.selectedOptions).map(option => option.value);
      onChangeRef.current(multiple ? selectedValues : selectedValues[0] ?? '');
    };

    if (!$?.fn?.chosen) {
      console.warn('Chosen is unavailable; using a native filter select.', select.id);
      setUsesNativeSelect(true);
      select.addEventListener('change', handleChange);
      return () => select.removeEventListener('change', handleChange);
    }

    setUsesNativeSelect(false);
    const $select = $(select);
    $select.chosen({
      allow_single_deselect: false,
      display_selected_options: false,
      search_contains: true,
      width: '100%',
    });
    chosenRef.current = $select;
    $select.on('change', handleChange);

    return () => {
      $select.off('change', handleChange);
      if ($select.data('chosen')) $select.chosen('destroy');
      chosenRef.current = null;
    };
  }, [multiple]);

  useEffect(() => {
    const select = selectRef.current;
    if (!select) return;

    const selectedValues = new Set((multiple ? value ?? [] : [value ?? '']).map(String));
    Array.from(select.options).forEach(option => {
      option.selected = selectedValues.has(option.value);
    });
    chosenRef.current?.trigger('chosen:updated');
  }, [value, options, multiple]);

  return (
    <div className="chosen-select-control">
      <select
        ref={selectRef}
        id={id}
        name={id}
        multiple={multiple}
        className={`form-control ${className}`}
        data-placeholder={placeholder}
        data-testid={testId}
        aria-describedby={usesNativeSelect ? `${id}-fallback-notice` : undefined}
        defaultValue={multiple ? value ?? [] : value ?? ''}>
        {!multiple && <option value="" />}
        {renderGroup
          ? renderGroup(options, renderOption)
          : options.map(option => (
            <option key={String(optionValue(option))} value={optionValue(option)}>{renderOption(option)}</option>
          ))}
      </select>
      {usesNativeSelect && (
        <small id={`${id}-fallback-notice`} className="chosen-select-control__notice text-muted"
          role="status" data-testid={`${testId}-fallback-notice`}>
          Enhanced search is unavailable. Use the standard selection list.
        </small>
      )}
    </div>
  );
};

ChosenSelect.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
  multiple: PropTypes.bool,
  className: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  renderOption: PropTypes.func,
  renderGroup: PropTypes.func,
  testId: PropTypes.string.isRequired,
  id: PropTypes.string,
};

export default ChosenSelect;